import { useEffect, useRef, useState } from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Switch,
  Typography,
  useTheme,
} from "@material-ui/core";
import { Link as RouterLink } from "react-router-dom";
import ChevronRightIcon from "../../icons/ChevronRight";
import CustomSkeleton from "src/components/skeleton/Skeleton";
import * as Yup from "yup";
import FormikForm, {
  FormikStep,
} from "src/components/ui-components/FormikForm";
import InputField from "src/components/ui-components/InputField";
import SelectField from "src/components/ui-components/SelectField";
import { AppSetting } from "./Types/AppSetting";
import {
  getAppSetting,
  getCategoryMaster,
  getCurrencies,
  getDateMaster,
  getInitLoadWidgets,
  getLanguagesForAppKey,
  getReadStatusMaster,
  insertUpdateAppSetting,
  getPageGroupsAndPages,
  getVoiceNavShapes
} from "./ConnectionService";
import { Language } from "./Types/Language";
import { Currency } from "./Types/Currency";
import { InitLoadWidget } from "./Types/InitLoadWidget";
import {
  CategoryMasterType,
  DateWindowMasterType,
  ReadStatusMasterType,
} from "./Types/MasterTypes";
import DateSelectField from "./DateSelectField";
import { Helmet } from "react-helmet-async";
import AlertMessage from "src/components/ui-components/AlertMessage";
import { ContentCopy } from "@material-ui/icons";
import SwitchField from "src/components/ui-components/SwitchField";

const defaultForm = {
  AppKey: null,
  CustomerName: null,
  LicenseCode: null,
  DefaultLanguageCode: null,
  DefaultCurrency: null,
  DefaultRegionCode: null,
  DefaultDateWindow: null,
  DefaultReadStatus: null,
  DefaultCategory: null,
  DefaultFromDateWindow: null,
  IsProfileAPICallRequired: false,
  ShowFooterNavigation: false,
  ShowBurgerNavigation: false,
  AppTitle: null,
  AppTitleTag: null,
  LandingPage: null,
  DefaultWidgetTagMatchAll: null,
  DefaultStyleKey: null,
  DefaultAuthKey: null,
  Logo: null,
  ScreenshotSecurityRequired: false,
  RootJailbreakSecurityRequired: false,
  ScreenLockSecurityRequired: false,
  IsVoiceNavigationRequired: true,
  ShapeType: null
};

export const DefaultWidgetTagMatchAllOptions = [
  {
    Label: "Match All",
    Value: true
  },
  {
    Label: "Don't Match All",
    Value: false
  }
];

const AppSettingsPage = () => {
  // const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isVoiceNavEnabled, setIsVoiceNavEnabled] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [initialAppSettingData, setInitialAppSettingData] = useState<AppSetting>(null);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [countries, setCountries] = useState([]);
  const [initWidgets, setInitWidgets] = useState<InitLoadWidget[]>([]);
  const [dateWindowMaster, setDateWindowMaster] = useState<DateWindowMasterType[]>([]);
  const [readStatusMaster, setReadStatusMaster] = useState<ReadStatusMasterType[]>([]);
  const [categoryMaster, setCategoryMaster] = useState<CategoryMasterType[]>([]);
  const [landingPageOptions, setLandingPageOptions] = useState([]);
  const [IsProfileAPICallRequired, setIsProfileAPICallRequired] = useState<boolean>(false);
  const appSettingsFormRef = useRef(null);
  // const navigate = useNavigate();
  const theme = useTheme();
  const [shapes, setShapes] = useState([]);
  // const columns: IColumn[] = [
  //   {
  //     field: "DisplayName",
  //     headerName: "WidgetName",
  //     width: "30%",
  //   },
  //   {
  //     field: "WebMethodURL",
  //     headerName: "URL",
  //     showDataAsList: true,
  //   },
  // ];

  const resetFormWithInitialData = async (data = null) => {
    if (appSettingsFormRef && appSettingsFormRef.current) {
     
      setIsVoiceNavEnabled(data.IsVoiceNavigationRequired)
      let formValues = defaultForm;
      if (data) {
        formValues = {
          AppKey: data.AppKey,
          CustomerName: data.CustomerName,
          LicenseCode: data.LicenseCode,
          DefaultLanguageCode: data.DefaultLanguageCode,
          DefaultCurrency: data.DefaultCurrency,
          DefaultRegionCode: data.DefaultRegionCode,
          DefaultDateWindow: data.DefaultDateWindow,
          DefaultReadStatus: data.DefaultReadStatus?.toString(),
          DefaultCategory: data.DefaultCategory,
          DefaultFromDateWindow: data.DefaultFromDateWindow,
          IsProfileAPICallRequired: data.IsProfileAPICallRequired,
          ShowFooterNavigation: data.ShowFooterNavigation ?? false,
          ShowBurgerNavigation: data.ShowBurgerNavigation ?? true,
          AppTitle: data.AppTitle,
          AppTitleTag: data.AppTitleTag,
          LandingPage: data.LandingPage,
          DefaultWidgetTagMatchAll: data.DefaultWidgetTagMatchAll,
          DefaultStyleKey: null,
          DefaultAuthKey: null,
          Logo: null,
          IsVoiceNavigationRequired: data.IsVoiceNavigationRequired,
          ShapeType: data.ShapeType,
          ScreenshotSecurityRequired: data.ScreenshotSecurityRequired,
          RootJailbreakSecurityRequired: data.RootJailbreakSecurityRequired,
          ScreenLockSecurityRequired: data.ScreenLockSecurityRequired
        };
      } else if (initialAppSettingData) {
        formValues = {
          AppKey: initialAppSettingData.AppKey,
          CustomerName: initialAppSettingData.CustomerName,
          LicenseCode: initialAppSettingData.LicenseCode,
          DefaultLanguageCode: initialAppSettingData.DefaultLanguageCode,
          DefaultCurrency: initialAppSettingData.DefaultCurrency,
          DefaultRegionCode: initialAppSettingData.DefaultRegionCode,
          DefaultDateWindow: initialAppSettingData.DefaultDateWindow,
          DefaultReadStatus: initialAppSettingData.DefaultReadStatus?.toString(),
          DefaultCategory: initialAppSettingData.DefaultCategory,
          DefaultFromDateWindow: initialAppSettingData.DefaultFromDateWindow,
          IsProfileAPICallRequired: initialAppSettingData.IsProfileAPICallRequired,
          ShowFooterNavigation: initialAppSettingData.ShowFooterNavigation ?? false,
          ShowBurgerNavigation: initialAppSettingData.ShowBurgerNavigation ?? true,
          AppTitle: initialAppSettingData.AppTitle,
          AppTitleTag: initialAppSettingData.AppTitleTag,
          LandingPage: initialAppSettingData.LandingPage,
          DefaultWidgetTagMatchAll: initialAppSettingData.DefaultWidgetTagMatchAll,
          DefaultStyleKey: null,
          DefaultAuthKey: null,
          Logo: null,
          IsVoiceNavigationRequired: data.IsVoiceNavigationRequired,
          ShapeType: data.ShapeType,
          ScreenshotSecurityRequired: initialAppSettingData.ScreenshotSecurityRequired,
          RootJailbreakSecurityRequired: initialAppSettingData.RootJailbreakSecurityRequired,
          ScreenLockSecurityRequired: initialAppSettingData.ScreenLockSecurityRequired
        };
      }
      setIsProfileAPICallRequired(formValues.IsProfileAPICallRequired);
      await appSettingsFormRef.current.resetForm({
        values: formValues,
        touched: {
          AppKey: false,
          CustomerName: false,
          LicenseCode: false,
          DefaultLanguageCode: false,
          DefaultCurrency: false,
          DefaultRegionCode: false,
          DefaultDateWindow: false,
          DefaultReadStatus: false,
          DefaultCategory: false,
          DefaultFromDateWindow: false,
          IsProfileAPICallRequired: false,
          ShowFooterNavigation: false,
          ShowBurgerNavigation: false,
          AppTitle: false,
          AppTitleTag: false,
          LandingPage: false,
          DefaultWidgetTagMatchAll: false,
          ScreenshotSecurityRequired: false,
          RootJailbreakSecurityRequired: false,
          ScreenLockSecurityRequired: false,
          IsVoiceNavigationRequired: true,
          ShapeType: false
        },
      });
    }
  };

  const submitForm = () => {
    if (appSettingsFormRef && appSettingsFormRef.current && !appSettingsFormRef.current.isSubmitting) {
      appSettingsFormRef.current.submitForm();
    }
  };

  const handleCancelClick = async () => {
    await resetFormWithInitialData();
    setIsEditing((prev) => !prev);
  };

  const populateAppSettingsData = async () => {
    const appSetting = await getAppSetting();
    setInitialAppSettingData(appSetting);
    await resetFormWithInitialData(appSetting);
  };
  const handleVoiceNav = (e) => {
    debugger
    setIsVoiceNavEnabled(e.target.checked)
    appSettingsFormRef?.current?.setFieldValue('IsVoiceNavigationRequired', e.target.checked);
  }
  const handleProfileToggleChange = (event) => {
    if (event.target.checked) {
      if (!initWidgets.some(x => x.DisplayName === 'userProfile')) {
        AlertMessage({ type: 'error', msg: 'Cannot enable without an initial widget \'userProfile\'' })
      } else {
        appSettingsFormRef?.current?.setFieldValue('IsProfileAPICallRequired', event.target.checked);
        setIsProfileAPICallRequired(event.target.checked);
      }
    } else {
      appSettingsFormRef?.current?.setFieldValue('IsProfileAPICallRequired', event.target.checked);
      setIsProfileAPICallRequired(event.target.checked);
    }
  };


  const fetchDLLData = async () => {
    const widgetRes = await getInitLoadWidgets();
    let list = [];
    widgetRes.forEach((x) => {
      if (list.some((y) => y.WidgetKey === x.WidgetKey)) {
        const index = list.findIndex((y) => y.WidgetKey === x.WidgetKey);
        list[index] = {
          ...list[index],
          WebMethodURL: list[index].WebMethodURL + ", " + x.WebMethodURL,
        };
      } else {
        list.push(x);
      }
    });
    setInitWidgets(list);

    const languageRes = await getLanguagesForAppKey();
    setLanguages(languageRes);
    let countriesTempList = [
      ...new Set(
        languageRes.map((x: { country: string; regionCode: string }) =>
          JSON.stringify({ country: x.country, regionCode: x.regionCode })
        )
      ),
    ].map((obj: string) => JSON.parse(obj));
    countriesTempList = countriesTempList?.sort((a, b) => {
      if (a.country < b.country) {
        return -1;
      }
      if (a.country > b.country) {
        return 1;
      }
      return 0;
    });
    setCountries(countriesTempList);

    const dateMasterRes = await getDateMaster();
    setDateWindowMaster(dateMasterRes);

    const readStatusMasterRes = await getReadStatusMaster();
    setReadStatusMaster(readStatusMasterRes);

    const categoryMasterRes = await getCategoryMaster();
    setCategoryMaster(categoryMasterRes);

    const currencyMasterRes = await getCurrencies();
    setCurrencies(currencyMasterRes);

    const pageGroupAndPagesOptions = await getPageGroupsAndPages();

    setLandingPageOptions(pageGroupAndPagesOptions);

    const voiceNavShapes = await getVoiceNavShapes();
    setShapes(voiceNavShapes);
  };

  const fetchData = async () => {
    // setIsFetching(true);
    setIsLoading(true);
    await fetchDLLData();
    await populateAppSettingsData();
    // setIsFetching(false);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Helmet>
        <title>Application Settings</title>
      </Helmet>
      <Box sx={{ minHeight: "100vh", pb: 4 }}>
        <Container maxWidth={false}>
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1100,
              opacity: 1,
              width: "100%",
              py: 2,
              backgroundColor: theme.palette.background.default,
            }}
          >
            <Grid
              alignItems="center"
              container
              justifyContent="space-between"
              sx={{ width: "100%", opacity: 1 }}
            >
              <Grid item>
                <Breadcrumbs
                  aria-label="breadcrumb"
                  separator={<ChevronRightIcon fontSize="small" />}
                >
                  <Link
                    color="textPrimary"
                    component={RouterLink}
                    to=""
                    variant="h5"
                  >
                    Configurations
                  </Link>
                  <Typography color="textPrimary" variant="h5">
                    Application Settings
                  </Typography>
                </Breadcrumbs>
              </Grid>
              <Grid item>
                <Grid alignItems="center" container spacing={2}>
                  {isEditing ? (
                    <>
                      <Grid item>
                        <Button
                          color="secondary"
                          sx={{
                            backgroundColor: "text.secondary",
                            color: "success.contrastText",
                          }}
                          type="button"
                          disabled={appSettingsFormRef?.current?.isSubmitting}
                          variant="contained"
                          onClick={handleCancelClick}
                        >
                          Cancel
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button
                          onClick={submitForm}
                          variant="contained"
                          sx={{
                            background:
                              "linear-gradient(to bottom, #072f40, #072f40)",
                          }}
                        >
                          Save
                          {appSettingsFormRef?.current?.isSubmitting && (
                            <CircularProgress
                              color="inherit"
                              thickness={5}
                              size={20}
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Button>
                      </Grid>
                    </>
                  ) : (
                    <Grid item>
                      <Button
                        onClick={() => setIsEditing((prev) => !prev)}
                        type="button"
                        variant="contained"
                        sx={{
                          background:
                            "linear-gradient(to bottom, #072f40, #072f40)",
                        }}
                      >
                        Edit
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Box>
          <Card>
            <CardContent>
              {isLoading && <CustomSkeleton skeletonType="FormEdit" />}
              <Box sx={{ display: isLoading ? "none" : "" }}>
                <FormikForm
                  innerRef={appSettingsFormRef}
                  initialValues={defaultForm}
                  enableReinitialize
                  onSubmit={async (values: AppSetting, { setSubmitting }) => {
                    setSubmitting(true);
                    setIsLoading(true);
                    const result = await insertUpdateAppSetting(values);
                    if (result) {
                      setIsEditing(false);
                      resetFormWithInitialData(values);
                      setInitialAppSettingData(values);
                    }
                    setSubmitting(false);
                    setIsLoading(false);
                  }}
                >
                  <FormikStep
                    validationSchema={Yup.object({
                      AppKey: Yup.string()
                        .nullable()
                        .required("AppKey is required")
                        .min(36, "AppKey must be 36 characters")
                        .max(36, "AppKey must be 36 characters")
                        .matches(
                          /^[a-zA-Z0-9-]+$/,
                          "Please enter valid AppKey"
                        ),
                      CustomerName: Yup.string()
                        .nullable()
                        .required("Name is required")
                        .max(100, "Name cannot be more than 100 characters")
                        .matches(
                          /^[a-zA-Z0-9](?!.* {2})[a-zA-Z0-9- ]*[a-zA-Z0-9]$/,
                          "Please enter a valid Name"
                        )
                        .test(
                          "not-all-same-character",
                          "Please enter a valid Name",
                          (value) =>
                            value && !/^(\S)\1*$/.test(value.replace(/ /g, ""))
                        ),
                      LicenseCode: Yup.string()
                        .nullable()
                        .required("License Code is required"),
                        // .max(
                        //   50,
                        //   "License Code cannot be more than 50 characters"
                        // )
                        // .matches(
                        //   /^[a-zA-Z0-9](?!.* {2})[a-zA-Z0-9- ]*[a-zA-Z0-9]$/,
                        //   "Please enter a valid License Code"
                        // )
                        // .test(
                        //   "not-all-same-character",
                        //   "Please enter a valid License Code",
                        //   (value) =>
                        //     value && !/^(\S)\1*$/.test(value.replace(/ /g, ""))
                        // ),
                      DefaultLanguageCode: Yup.string()
                        .nullable()
                        .required("Default Language is required"),
                      DefaultCurrency: Yup.string()
                        .nullable()
                        .required("Default Currency is required"),
                      DefaultRegionCode: Yup.string().nullable(),
                      DefaultDateWindow: Yup.string().nullable(),
                      DefaultReadStatus: Yup.string().nullable(),
                      DefaultCategory: Yup.string().nullable(),
                      DefaultFromDateWindow: Yup.string().nullable(),
                      IsProfileAPICallRequired: Yup.string()
                        .nullable()
                        .required("Required"),
                      ShowBurgerNavigation: Yup.string().nullable(),
                      ShowFooterNavigation: Yup.string().nullable(),
                      AppTitle: Yup.string().nullable(),
                      AppTitleTag: Yup.string().nullable(),
                      LandingPage: Yup.string().nullable(),
                      DefaultWidgetTagMatchAll: Yup.boolean().required('This field is required').nullable(),
                      RootJailbreakSecurityRequired: Yup.bool().required("Required"),
                      ScreenLockSecurityRequired: Yup.bool().required("Required"),
                      ScreenshotSecurityRequired: Yup.bool().required("Required"),                     
                      ShapeType: Yup.string().nullable()
                    })}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                        <InputField
                          label="AppKey"
                          name="AppKey"
                          disabled
                          onChange={() => { }}
                          InputLabelProps={{
                            shrink:
                              !!appSettingsFormRef?.current?.values?.AppKey,
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={() => navigator.clipboard.writeText(appSettingsFormRef?.current?.values?.AppKey)} sx={{ paddingX: '1%', paddingY: '0' }}>
                                  <ContentCopy sx={{ color: '#072f40' }} fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                        <InputField
                          label="Name *"
                          name="CustomerName"
                          disabled={!isEditing}
                          InputLabelProps={{
                            shrink:
                              !!appSettingsFormRef?.current?.values
                                ?.CustomerName,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                        <InputField
                          label="License Code *"
                          name="LicenseCode"
                          disabled={true}
                          InputLabelProps={{
                            shrink:
                              !!appSettingsFormRef?.current?.values
                                ?.LicenseCode,
                          }}
                          onChange={() => {}}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                        <SelectField
                          data={languages}
                          dataTextField={`language`}
                          dataValueField="languageCode"
                          name="DefaultLanguageCode"
                          label="Default Language *"
                          disable={!isEditing}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                        <SelectField
                          data={currencies}
                          dataTextField="CurrencyCode"
                          dataValueField="CurrencyCode2"
                          name="DefaultCurrency"
                          label="Default Currency *"
                          disable={!isEditing}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                        <SelectField
                          data={countries}
                          dataTextField="country"
                          dataValueField="regionCode"
                          name="DefaultRegionCode"
                          label="Default Region"
                          disable={!isEditing}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                        <DateSelectField
                          data={dateWindowMaster}
                          getOptionLabel={(option) =>
                            `${option.Description}(${option.DateWindow})`
                          }
                          dataValueField="DateWindow"
                          name="DefaultDateWindow"
                          label="Default Date Window"
                          disable={!isEditing}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                        <DateSelectField
                          data={readStatusMaster}
                          getOptionLabel={(option) =>
                            `${option.Description}(${option.ReadStatusKey})`
                          }
                          dataValueField="Description"
                          name="DefaultReadStatus"
                          label="Default Read Status"
                          disable={!isEditing}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                        <SelectField
                          data={categoryMaster}
                          dataTextField="CategoryKey"
                          dataValueField="Description"
                          name="DefaultCategory"
                          label="Default Category"
                          disable={!isEditing}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                        <DateSelectField
                          data={dateWindowMaster}
                          getOptionLabel={(option) =>
                            `${option.Description}(${option.DateWindow})`
                          }
                          dataValueField="DateWindow"
                          name="DefaultFromDateWindow"
                          label="Default From Date Window"
                          disable={!isEditing}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                        <InputField
                          label="App Title"
                          name="AppTitle"
                          disabled={!isEditing}
                          InputLabelProps={{
                            shrink: !!appSettingsFormRef?.current?.values?.AppTitle,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                        <InputField
                          label="App Title Tag"
                          name="AppTitleTag"
                          disabled={!isEditing}
                          InputLabelProps={{
                            shrink: !!appSettingsFormRef?.current?.values?.AppTitleTag,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                        <SelectField
                          data={landingPageOptions}
                          dataTextField="Label"
                          groupBy={(option) => option.PageGroupName}
                          dataValueField="Value"
                          name="LandingPage"
                          label="Landing Page"
                          disable={!isEditing}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                        <SelectField
                          data={DefaultWidgetTagMatchAllOptions}
                          dataTextField='Label'
                          dataValueField="Value"
                          name="DefaultWidgetTagMatchAll"
                          label="Default Widget Tag Match"
                          disable={!isEditing}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                        <InputField
                          label="Logo"
                          name="Logo"
                          type="file"
                          disabled
                          onClick={() => { }}
                          id="logoFileBox"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4} xl={2}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={IsProfileAPICallRequired}
                              onChange={(event) => handleProfileToggleChange(event)}
                              disabled={!isEditing}
                            />
                          }
                          label="Profile API Call Required"
                          labelPlacement='start'
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>

                        <FormControlLabel
                          control={
                            <Switch
                              checked={isVoiceNavEnabled}
                              onChange={(event) => handleVoiceNav(event)}
                              disabled={!isEditing}
                            />
                          }
                          label="Voice Navigation Required"
                          labelPlacement='start'
                        />

                        
                      </Grid>
                      {isVoiceNavEnabled && (
                        <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                          <SelectField
                            data={shapes}
                            dataTextField="ShapeType"
                            dataValueField="VoiceNavigationShapeMasterKey"
                            name="ShapeType"
                            label="Icon Shapes"
                            disable={!isEditing}
                          />
                        </Grid>
                      )}


                      

                      <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                        <SwitchField name="ScreenshotSecurityRequired" label="Screenshot Security Required" disabled={!isEditing} />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                        <SwitchField name="RootJailbreakSecurityRequired" label="Root Jail Break Security Required" disabled={!isEditing} />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                        <SwitchField name="ScreenLockSecurityRequired" label="Screen Lock Security Required" disabled={!isEditing} />
                      </Grid>
                    </Grid>
                  </FormikStep>
                </FormikForm>
              </Box>
            </CardContent>
          </Card>
          {/* <Card sx={{ marginTop: 2 }}>
            <CardContent>
              <InitWidgetsTable
                columns={columns}
                dataList={initWidgets}
                isLoading={isFetching}
                onClickEdit={(data: any) =>
                  navigate("/widget/InsertUpdateWidget", {
                    state: { widgetKey: data.WidgetKey },
                  })
                }
                showActionsHeader
              />
            </CardContent>
          </Card> */}
        </Container>
      </Box>
    </>
  );
};

export default AppSettingsPage;
